import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import CheckboxGroup from '@alltrails/denali/components/CheckboxGroup';
import { CheckboxFilterConfig, FilterConfig, RadioFilterConfig } from '@alltrails/denali/components/FilterBar';
import Link from '@alltrails/denali/components/Link';
import RadioGroup from '@alltrails/denali/components/RadioGroup';
import { RangeSlider, Slider } from '@alltrails/denali/components/Slider';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import styles from './FilterControl.module.scss';

const collapsedOptionCount = 4;

type SelectControlProps = {
  filterConfig: CheckboxFilterConfig | RadioFilterConfig;
};

const getLabel = (label?: ReactNode, purchaseTier?: PurchaseTier) => {
  if (!label) {
    return undefined;
  }

  if (purchaseTier === 'plus') {
    return (
      <span className={styles.labelContainer}>
        <PlusLogoBadge variant="icon" size="sm" />
        {label}
      </span>
    );
  }

  return label;
};

const SelectFilterControl = ({ filterConfig }: SelectControlProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const testId = filterConfig.type === 'radio' ? filterConfig.radioGroup.testId : filterConfig.checkboxGroup.testId;
  const options = filterConfig.type === 'radio' ? filterConfig.radioGroup.options : filterConfig.checkboxGroup.options;
  const canExpand = options.length > collapsedOptionCount;
  const expandCta = canExpand ? (
    <Link className={styles.expandCta} onClick={() => setIsExpanded(!isExpanded)} size="md" testId={`${testId}-${isExpanded ? 'less' : 'more'}`}>
      {isExpanded ? <FormattedMessage defaultMessage="Less" /> : <FormattedMessage defaultMessage="More" />}
    </Link>
  ) : null;

  // any cast is safe since we aren't altering the options at all
  const renderedOptions: any = canExpand && !isExpanded ? options.slice(0, collapsedOptionCount) : options;

  const element =
    filterConfig.type === 'radio' ? (
      <RadioGroup
        {...filterConfig.radioGroup}
        labelText={getLabel(filterConfig.radioGroup.labelText, filterConfig.purchaseTier)}
        labelTextVariant="heading300"
        options={renderedOptions}
      />
    ) : (
      <CheckboxGroup
        {...filterConfig.checkboxGroup}
        labelText={getLabel(filterConfig.checkboxGroup.labelText, filterConfig.purchaseTier)}
        labelTextVariant="heading300"
        options={renderedOptions}
      />
    );

  return (
    <>
      {element}
      {filterConfig.additionalContent}
      {expandCta}
    </>
  );
};

type FilterControlProps = {
  filterConfig: FilterConfig;
};

const FilterControl = ({ filterConfig }: FilterControlProps) => {
  switch (filterConfig.type) {
    case 'checkbox':
    case 'radio':
      return <SelectFilterControl filterConfig={filterConfig} />;
    case 'custom-dropdown':
      return <>{filterConfig.content}</>;
    case 'range-slider':
      return (
        <RangeSlider
          {...filterConfig.rangeSlider}
          label={getLabel(filterConfig.rangeSlider.label, filterConfig.purchaseTier)}
          headerValueTextVariant="text200"
          labelTextVariant="heading300"
        />
      );
    case 'slider':
      return (
        <Slider
          {...filterConfig.slider}
          label={getLabel(filterConfig.slider.label, filterConfig.purchaseTier)}
          headerValueTextVariant="text200"
          labelTextVariant="heading300"
        />
      );
    default:
      return null;
  }
};

export default FilterControl;
